import * as uuid from "uuid"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import {
    CategoryDetailData,
    CategoryDetailDataImage,
    CategoryDetailResponse,
    CategoryImageVariantDto,
    InItemModifier,
    InItemModifierGroup,
    Override,
} from "../../../../graphql/types"
import type { CategoryFormData } from "./CategoryForm"
import { UnmatchedOrderItemNamesResponse } from "./detail/request"
import { difference, prop, sortBy } from "ramda"
import { MoveGroupDirection } from "./OptionAndModifierTableHeader"
import { AssignedModifierGroupTableItem } from "./AssignedModifierGroupTable"
import { HorizontalTab } from "../../../uiKit/HorizontalTabs/HorizontalTabs"
import { ItemOverrideData, ItemOverrideV2, OverrideContext } from "./detail/types"
import { TerminalAuthData } from "../../../auth/types"
import { ItemDetailIncreasePriceInfo } from "./types"
import { ProductTaxesTableItem } from "../components/ProductTaxesTable"
import { ProductPricingsTableItem } from "../components/ProductPricingsTable"
import { CropType, ImageUploadData } from "../../../uiKit/ImageUpload/types"
import { appendAssetsHostnameToUrl, stripHostnameFromAssetsUrl } from "../../../../utils/environment"

export const parsePriceValue = (price: Maybe<number>, otherwise: any = null) => {
    if (price == null || price === 0) {
        return otherwise
    }

    return parseFloat((price * 100).toFixed(0))
}

export const getNewParentId = (
    formParentId: Maybe<string>,
    formData: Maybe<CategoryFormData>,
    rootCategoryId: string
) => {
    if (formData && formData.categoryId === rootCategoryId) {
        // If opened item is Root category (via Sales input), return null, no parentId
        return null
    } else if (formParentId) {
        // If parentId is formValue, ie. some parent category was selected from Category Picker
        return formParentId
    } else {
        // If no category was selected from category picker and item is not root
        return rootCategoryId
    }
}

export const getAltNamesAutocompleteOptions = (
    initialValue: Maybe<string[]>,
    currentValue: Maybe<string[]>,
    unmatchedItems: UnmatchedOrderItemNamesResponse
): UnmatchedOrderItemNamesResponse => {
    const newInitialValue = initialValue || []
    const newCurrentValue = currentValue || newInitialValue
    return unmatchedItems
        ? [
              ...unmatchedItems,
              // Display also alternative names that were removed during editing the item
              ...difference(newInitialValue, newCurrentValue),
          ]
        : null
}

export const isUnitBasedPricingType = (priceType: Maybe<string>): boolean => {
    return priceType === "unit-based" || priceType === "catering"
}

export const moveGroup = (
    groupId: string,
    direction: MoveGroupDirection,
    groups: AssignedModifierGroupTableItem[]
): AssignedModifierGroupTableItem[] => {
    const currentIdx = groups.findIndex(g => g.id === groupId)
    const group = groups[currentIdx]
    const newGroups = [...groups]

    if (!group) {
        return groups
    }

    if (direction === "top") {
        return [group, ...groups.filter(g => g.id !== groupId)]
    }
    if (direction === "up") {
        const newIdx = Math.max(0, currentIdx - 1)
        const prevGroup = groups[newIdx]
        newGroups[newIdx] = group
        newGroups[currentIdx] = prevGroup

        return newGroups
    }
    if (direction === "down") {
        const newIdx = Math.min(groups.length - 1, currentIdx + 1)
        const prevGroup = groups[newIdx]
        newGroups[newIdx] = group
        newGroups[currentIdx] = prevGroup

        return newGroups
    }
    if (direction === "bottom") {
        return [...groups.filter(g => g.id !== groupId), group]
    }
    return groups
}

// Copied from business config
export const constructPrice = (
    price: number,
    priceMultiplier: Maybe<number> = 1,
    shouldRoundUpPrices: boolean = true
): number => {
    if (priceMultiplier === 1 || priceMultiplier === null) {
        return price
    }
    const calculatedPrice = price * priceMultiplier

    if (!shouldRoundUpPrices) {
        return Math.ceil(calculatedPrice)
    }

    const dollars = parseInt((calculatedPrice / 100).toString())
    const cents = Math.floor(calculatedPrice % 100)
    // I don't like this but according to some discussions price like $2.06 should be rounded to $2
    if (cents <= 9) {
        return dollars * 100
    }
    if (cents <= 49) {
        return dollars * 100 + 49
    }
    if (cents <= 99) {
        return dollars * 100 + 99
    }
    throw new Error("constructPrice - invalid cents!")
}

export enum ItemDetailTab {
    Pricing = "pricing",
    AdditionalInfo = "additional-info",
    History = "history",
}

export const itemDetailTabs: HorizontalTab[] = [
    {
        id: ItemDetailTab.Pricing,
        label: "Pricing",
    },
    {
        id: ItemDetailTab.AdditionalInfo,
        label: "Additional Info",
    },
    {
        id: ItemDetailTab.History,
        label: "History",
        isDisabled: true,
    },
]

export const constructOverridesData = (
    overrides: Override[],
    aggregatorTerminals: TerminalAuthData[],
    category: CategoryDetailData
) => {
    return aggregatorTerminals
        .map(t => {
            const isOptionBased = category.optionGroup != null
            const isFixedPrice = !isOptionBased && category.fixedPrice != null
            const overrideData = overrides.find(o => o.contextId === t.id)?.data as Maybe<ItemOverrideData>

            if (isOptionBased && category.optionGroup) {
                // Get overrides options.
                // But if category has different option group, or option does not exist in category, filter it out.
                const overrideOptions =
                    overrideData?.optionGroup?.id && overrideData.optionGroup.id === category.optionGroup.id
                        ? overrideData.optionGroup.options.filter(o => {
                              const optionExists = category.optionGroup?.options.find(co => co.id === o.id)
                              return !!optionExists
                          })
                        : []

                return {
                    context: OverrideContext.Terminal,
                    contextId: t.id,
                    data: {
                        optionGroup: {
                            id: category.optionGroup.id,
                            isActive: true,
                            options: category.optionGroup.options.map(o => {
                                const overrideOption = overrideOptions.find(oo => oo.id === o.id)
                                return {
                                    id: o.id,
                                    price: overrideOption?.price ?? undefined,
                                }
                            }),
                        },
                    },
                }
            } else if (isFixedPrice) {
                return {
                    context: OverrideContext.Terminal,
                    contextId: t.id,
                    data: {
                        price: overrideData?.price ?? undefined,
                    },
                }
            }

            return null
        })
        .filter(Boolean) as ItemOverrideV2[]
}

export const calcIncreasedItemDetailPrice = (
    inputPrice: Maybe<number>, // 5.55 = $5.55
    priceIncreaseInfo: Maybe<ItemDetailIncreasePriceInfo>
): Maybe<number> => {
    if (!priceIncreaseInfo || inputPrice == null) {
        return null
    }

    const percentage = priceIncreaseInfo.percentage / 1000
    return Math.round((inputPrice + inputPrice * (percentage / 100)) * 100)
}

export const constructCategoryFormData = (categoryResponse: Maybe<CategoryDetailResponse>): Maybe<CategoryFormData> => {
    if (!categoryResponse) {
        return null
    }

    const category: CategoryDetailData = categoryResponse.data

    if (!category) {
        return null
    }

    // @ts-expect-error
    const taxes: ProductTaxesTableItem[] = category.taxes.map(t => ({
        id: t.id,
        name: t.name,
        isActive: t.isActive,
        isActiveOverriden: false,
        isAutoApply: t.isAutoApply,
        isAutoApplyOverriden: false,
        isEmbedded: t.isEmbedded,
        isEmbeddedOverriden: false,
        parentOverride: t.parentOverride,
    }))
    // @ts-expect-error
    const pricings: ProductPricingsTableItem[] = category.pricings.map(t => ({
        id: t.id,
        name: t.name,
        isActive: t.isActive,
        isActiveOverriden: false,
        isAutoApply: t.isAutoApply,
        isAutoApplyOverriden: false,
        parentOverride: t.parentOverride,
    }))
    return {
        categoryId: category.id,
        // @ts-expect-error
        type: category.type,
        name: category.name,
        shortName: category.displayName,
        internalName: category.internalName,
        kitchenName: category.kitchenName,
        alternativeNames: category.alternativeNames,
        tags: category.tags,
        description: category.description,
        parentId: category.parentId,
        icon: category.icon,
        color: category.color,
        isActive: category.isActive,
        price: category.fixedPrice != null ? category.fixedPrice / 100 : null,
        newlyCreatedGroupId: null,
        categoryImagesNew: category.images || [],
        unit: category.unit,
        tare: category.tare,
        pieces: category.pieces,
        containsAlcohol: category.containsAlcohol != null ? category.containsAlcohol : false,
        alwaysOpenPopover: category.alwaysOpenPopover != null ? category.alwaysOpenPopover : false,
        mibNoteDisabled: category.disableItemNoteMiB,
        mibNoteRequired: category.requireItemNoteMiB,
        mibNotePlaceholder: category.itemNoteMiBPlaceholder,
        kioskNoteDisabled: category.disableItemNoteKiosk,
        adjustablePrice: category.adjustablePricePOS,
        displayImagePOS: category.displayImagePOS,
        barcode: category.barcode,
        pluCode: category.pluCode,
        categoryTaxes: sortBy(prop("name"), taxes),
        categoryPricings: pricings,
        cardProgramId: category.cardProgramId,
        minQuantity: category.minQuantity,
        maxQuantity: category.maxQuantity,
        optionGroupNew: category.optionGroup
            ? {
                  id: category.optionGroup.id,
                  name: category.optionGroup.name,
                  isProminent: category.optionGroup.isProminent,
                  options: category.optionGroup.options.map(option => ({
                      id: option.id,
                      productId: option.productId,
                      name: option.name,
                      isPreset: option.isPreset,
                      isPresetOverriden: false,
                      price: option.price ? option.price / 100 : 0,
                      priceOverriden: false,
                      nestedCategoryGroupId: option.nestedCategoryGroupId,
                      isActive: true,
                  })),
              }
            : null,
        modifierGroupsNew: category.modifierGroups.map(
            (group: InItemModifierGroup): AssignedModifierGroupTableItem => ({
                id: group.id,
                name: group.name,
                isProminent: group.isProminent,
                isExclusion: group.isExclusion,
                modifiers: group.modifiers.map((modifier: InItemModifier) => ({
                    id: modifier.id,
                    productId: modifier.productId,
                    name: modifier.name,
                    isPreset: modifier.isPreset,
                    isPresetOverriden: false,
                    isMulti: modifier.isMulti,
                    isMultiOverriden: false,
                    isActive: true,
                    price: modifier.price,
                    priceOverriden: false,
                    groupExtra: modifier.supplement,
                    groupExtraOverriden: false,
                    nestedCategoryGroupId: modifier.nestedCategoryGroupId,
                })),
            })
        ),
    }
}

export const getImageUploadInputData = (itemImages: CategoryDetailDataImage[]): ImageUploadData[] => {
    return itemImages.map(i => ({
        originalImage: {
            id: i.id,
            url: appendAssetsHostnameToUrl(i.path) as string,
        },
        croppedVariants: i.variants?.map(v => ({
            url: appendAssetsHostnameToUrl(v.url) as string,
            cropType: v.croppedType as CropType,
            cropCoords: v.cropCoords,
        })),
    }))
}

export const getItemImgsFromImageUploadImages = (
    data: ImageUploadData[],
    existingImages: CategoryDetailDataImage[]
): CategoryDetailDataImage[] => {
    return data.map(i => ({
        id: i.originalImage.id,
        path: stripHostnameFromAssetsUrl(i.originalImage.url) as string,
        variants:
            i.croppedVariants?.map(v => {
                const existingVariant =
                    existingImages
                        .map(i =>
                            i.variants?.find(c => {
                                const oldUrl = appendAssetsHostnameToUrl(c.url) as string
                                return oldUrl === v.url
                            })
                        )
                        .filter((i): i is CategoryImageVariantDto => !!i)[0] ?? null
                return {
                    id: existingVariant?.id ?? uuid.v7(),
                    url: stripHostnameFromAssetsUrl(v.url) as string,
                    croppedType: v.cropType,
                    cropCoords: v.cropCoords,
                }
            }) ?? null,
    }))
}
